import type { Location } from "react-router-dom";
import type { RoutingPublicSubmitData } from "@/api/scheduling/routing-public.types";

const STORAGE_PREFIX = "notifyy.routingFormSubmission";

export interface RoutingSubmissionHandoff {
  routingFormPublicKey: string;
  bookingPublicKey: string;
  submissionId: number;
  submissionReference?: string | null;
  createdAt: number;
}

export interface RoutingSubmissionNavigationState {
  routingSubmission?: RoutingSubmissionHandoff;
}

const isNonNegativeInteger = (value: unknown): value is number =>
  typeof value === "number" && Number.isInteger(value) && value >= 0;

const getStorageKey = (bookingPublicKey: string): string =>
  `${STORAGE_PREFIX}:${bookingPublicKey}`;

const isRoutingSubmissionHandoff = (
  value: unknown,
): value is RoutingSubmissionHandoff => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const record = value as Record<string, unknown>;
  return (
    typeof record.routingFormPublicKey === "string" &&
    /^rf_[A-Za-z0-9_-]+$/.test(record.routingFormPublicKey) &&
    typeof record.bookingPublicKey === "string" &&
    /^evt_[A-Za-z0-9_-]+$/.test(record.bookingPublicKey) &&
    isNonNegativeInteger(record.submissionId) &&
    typeof record.createdAt === "number" &&
    Number.isFinite(record.createdAt) &&
    (record.submissionReference === undefined ||
      record.submissionReference === null ||
      typeof record.submissionReference === "string")
  );
};

export const buildRoutingSubmissionHandoff = (
  routingFormPublicKey: string,
  submitData: RoutingPublicSubmitData,
): RoutingSubmissionHandoff | null => {
  const bookingPublicKey = submitData.bookingPublicKey?.trim() || "";
  const routingPublicKey = routingFormPublicKey.trim();

  if (
    !/^rf_[A-Za-z0-9_-]+$/.test(routingPublicKey) ||
    !/^evt_[A-Za-z0-9_-]+$/.test(bookingPublicKey) ||
    !isNonNegativeInteger(submitData.submissionId)
  ) {
    return null;
  }

  return {
    routingFormPublicKey: routingPublicKey,
    bookingPublicKey,
    submissionId: submitData.submissionId,
    submissionReference: submitData.submissionReference ?? null,
    createdAt: Date.now(),
  };
};

export const saveRoutingSubmissionHandoff = (
  handoff: RoutingSubmissionHandoff,
): void => {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(
      getStorageKey(handoff.bookingPublicKey),
      JSON.stringify(handoff),
    );
  } catch {
    // Navigation state still carries the handoff when session storage is blocked.
  }
};

export const getRoutingSubmissionFromLocation = (
  location: Pick<Location, "state">,
  bookingPublicKey: string,
): RoutingSubmissionHandoff | null => {
  const state = location.state as RoutingSubmissionNavigationState | null;
  const handoff = state?.routingSubmission;
  return handoff?.bookingPublicKey === bookingPublicKey &&
    isRoutingSubmissionHandoff(handoff)
    ? handoff
    : null;
};

export const getStoredRoutingSubmissionHandoff = (
  bookingPublicKey: string,
): RoutingSubmissionHandoff | null => {
  if (typeof window === "undefined" || !bookingPublicKey) return null;
  try {
    const rawValue = window.sessionStorage.getItem(getStorageKey(bookingPublicKey));
    if (!rawValue) return null;
    const parsedValue = JSON.parse(rawValue) as unknown;
    return isRoutingSubmissionHandoff(parsedValue) ? parsedValue : null;
  } catch {
    return null;
  }
};

export const clearRoutingSubmissionHandoff = (bookingPublicKey: string): void => {
  if (typeof window === "undefined" || !bookingPublicKey) return;
  try {
    window.sessionStorage.removeItem(getStorageKey(bookingPublicKey));
  } catch {
    // Ignore storage cleanup failures; the booking request has already completed.
  }
};
